class ReleaseCalendar extends HTMLElement {
  constructor() {
    super();

    this.timezone = this.getAttribute('data-timezone') || 'UTC';

    const serverTimeAttr = this.getAttribute('data-server-time');
    this.serverTime = serverTimeAttr ? parseInt(serverTimeAttr, 10) : Date.now();

    this.localeUrl = window?.theme?.locale === 'fr_CA' ? '/fr' : '';

    this.articles = [];
    const scriptEl = document.getElementById('ReleaseCalendarArticles');

    if (scriptEl?.textContent) {
      try {
        this.articles = JSON.parse(scriptEl.textContent);
      } catch (err) {
        console.error('ReleaseCalendar JSON parse failed:', err);
      }
    }
    this.indexArticle = 0;
    this.lastIndex = this.articles.length;
    this.featuredItems = [];
    this.releasedItems = [];
    this._initialized = false;
  }

  /**
   * Desktop (≥750px, theme breakpoint): first 4 images eager/high; mobile: first 1 only (one slide per view).
   */
  getPriorityImageLimit() {
    return window.matchMedia('(min-width: 750px)').matches ? 4 : 1;
  }

  /**
   * After sorted slides are in the DOM, tune LCP-related image hints via HTMLImageElement IDL
   * properties (loading, fetchPriority, decoding)—same mechanism as setting attributes in markup.
   * If `limit` is omitted, uses getPriorityImageLimit() (4 desktop / 1 mobile).
   */
  applyPriorityImageAttrs(listRoot, limit) {
    if (!listRoot) return;
    const resolvedLimit =
      typeof limit === 'number' ? limit : this.getPriorityImageLimit();
    const imgs = listRoot.querySelectorAll(':scope > li img');
    imgs.forEach((img, index) => {
      const isPrioritySlot = index < resolvedLimit;
      img.loading = isPrioritySlot ? 'eager' : 'lazy';
      img.fetchPriority = isPrioritySlot ? 'high' : 'low';
      img.decoding = isPrioritySlot ? 'sync' : 'async';
    });
  }

  connectedCallback() {
    if (this._initialized) return;
    if (!Array.isArray(this.articles) || !this.articles.length) return;

    this._initialized = true;
    this.init();
  }

  init() {
    for (let i = 0; i < this.articles.length; i++) {
      this.setArticle(this.articles[i]);
    }
  }

  getArticle(article) {
    return fetch(
      `${this.localeUrl}/blogs/release-calendar/${article.handle}?section_id=release-calendar_data`
    )
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        const data = JSON.parse(
          html.getElementById('ReleaseCalendarData').textContent
        );

        this.setArticle(data);
      });
  }

  setArticle(data = {}) {
    const { product, article, accentuate } = data;
    const price = accentuate.price
      ? window.theme.locale === 'fr_CA'
        ? `: ${accentuate.price}$ CAD`
        : `: $${accentuate.price} CAD`
      : '';
    const style = accentuate.style_
      ? `<span>Style</span>: ${accentuate.style_}`
      : "";
    const sortOrder = this.getResolvedSortOrder({
      explicitOrder: accentuate.sort_order,
      releaseDay: accentuate.release_day,
      createdAt: article.created_at,
    });

    const featuredSortOrder = this.getResolvedSortOrder({
      explicitOrder: accentuate.featured_sort_order,
      releaseDay: accentuate.release_day,
      createdAt: article.created_at,
    });


    const today = new Date(this.serverTime);
    const action = this.getActionType(accentuate, product, today);

    if (
      accentuate.pin_product_to_featured == 'true' &&
      (accentuate.this_product_is_released_first == 'true' ||
        accentuate.this_product_is_getting_raffle == 'true')
    ) {
      const featuredItemsWrapper = document.createElement('div');
      const featuredImageWidths = [300, 350, 600, 700, 800, 900, 1050, 1200];

      const featuredImageSrcsetSizes = featuredImageWidths
        .map(w => `${article?.image?.src}&width=${w} ${w}w`)
        .join(',\n  ');
      const feature_item = `
        <li id="Slide-${
          article.id
        }" class='grid__item slider__slide'  data-featured-sort-order="${featuredSortOrder}">
          <div class="release__featured-item release__product">        
            <div class='media media--square'>
              <img
                src="${article.image.src}&width=600"
                srcset="${featuredImageSrcsetSizes}"
                sizes="(max-width: 375px) 349px, (max-width: 425px) 399px, (max-width: 768px) 169px, (max-width: 1024px) 233px, (max-width: 1440px) 290px, 290px"
                alt="${article.title}"
                loading="lazy"
                fetchpriority="low"
                decoding="async"
                width="${article?.image?.width}"
                height="${article?.image?.height}"
              />
            </div>
            <h3 class='release__featured-title'>${article?.title}</h3>
            <div class="featured__item-details">
              <div class="featured__item-info">
                ${style}
                ${
                  accentuate.price && accentuate.style_ ? "|" : ""
                } <span>${window.theme.locale === 'fr_CA' ? 'Prix' : 'Price'}</span> ${price}
              </div>
              ${action.button ? action.button : ""}
              ${
                accentuate.display_launch_details === "true"
                  ? `
                <modal-opener
                  class="featured__item-toggle"
                  data-modal="#FeaturedItemModal-${article.id}"
                >
                  <button class="link link--underlined">
                    ${window.location.pathname.includes('/fr')
                      ? window.releaseCalendarStrings.launchDetailsFr
                      : window.releaseCalendarStrings.launchDetails
                    }
                  </button>
                </modal-opener>

                <modal-dialog id="FeaturedItemModal-${
                  article.id
                }" class="modal">
                  <div
                    role="dialog"
                    aria-label="${article.title}"
                    aria-modal="true"
                    class="modal__container modal__container--small"
                    tabindex="-1"
                  >
                    <button
                      id="ModalClose-${article.id}"
                      type="button"
                      class="link modal__close"
                      aria-label="Close"
                    >
                      X
                    </button>
                    <div class="modal__content">
                      <div class='launch_details-modal'>
                        <h2>${window.location.pathname.includes('/fr') ? window.releaseCalendarStrings.modalLaunchDetailsTitleFr : window.releaseCalendarStrings.modalLaunchDetailsTitle}</h2>
                        ${
                          accentuate.launch_details_fr &&
                          window.location.pathname.includes('/fr')
                            ? accentuate.launch_details_fr
                            : accentuate.launch_details
                        }
                        ${action.button ? action.button : ''}
                      </div>
                    </div>
                  </div>
                </modal-dialog>
                `
                  : ''
              }
            </div>
          </div>
        </li>`;

      featuredItemsWrapper.innerHTML = feature_item;

      this.featuredItems.push(featuredItemsWrapper.querySelector("li"));
    }
    const releaseItemWidths = [100, 130, 200, 260, 300, 350, 400, 600, 700, 800, 900, 1050, 1200];

    const releaseItemSrcsetSizes = releaseItemWidths
      .map(w => `${article?.image?.src}&width=${w} ${w}w`)
      .join(',\n  ');
    const releaseItemsWrapper = document.createElement('div');
    const list_item = `
      <li
        id="Slide-${article?.id}"
        class="grid__item slider__slide release__item release__product ${
          action.closed_raffle
        }" style="order: ${sortOrder}">
        <div class="release__item-wrapper">  
          <div class="release__item-image-wrapper">
            <div class='media media--square'>
              <img
                src="${article?.image?.src}&width=600"
                srcset="${releaseItemSrcsetSizes}"
                sizes="(max-width: 768px) 101px, 131px"
                alt="${article?.title}"
                loading="lazy"
                fetchpriority="low"
                decoding="async"
                width="${article?.image?.width}"
                height="${article?.image?.height}"
              />
            </div>
          </div>
          <div class="release__item-row">
            <div class="release__item-details">
              <div class='release__item-info-wrapper'>
                <h3 class="release__item-title">${article.title}</h3>
                <div class="release__item-info">
                  ${style} ${
      accentuate.price && accentuate.style_ ? '|' : ''
    } <span>${window.theme.locale === 'fr_CA' ? 'Prix' : 'Price'}</span>  ${price}
                </div>
              </div>
              <div class='release__item-info-wrapper'>
                ${action.button}
                ${
                  accentuate.display_launch_details === 'true'
                    ? `
                  <modal-opener
                  class="featured__item-toggle"
                  data-modal="#FeaturedItemModal-${article.id}"
                >
                  <button class="link link--underlined">
                    ${window.location.pathname.includes('/fr')
                      ? window.releaseCalendarStrings.launchDetailsFr
                      : window.releaseCalendarStrings.launchDetails
                    }
                  </button>
                </modal-opener>

                <modal-dialog id="FeaturedItemModal-${
                  article.id
                }" class="modal">
                  <div
                    role="dialog"
                    aria-label="${article.title}"
                    aria-modal="true"
                    class="modal__container modal__container--small"
                    tabindex="-1"
                  >
                    <button
                      id="ModalClose-${article.id}"
                      type="button"
                      class="link modal__close"
                      aria-label="Close"
                    >
                      X
                    </button>
                    <div class="modal__content">
                      <div class='launch_details-modal'>
                        <h2>${window.location.pathname.includes('/fr') ? window.releaseCalendarStrings.modalLaunchDetailsTitleFr : window.releaseCalendarStrings.modalLaunchDetailsTitle}</h2>
                        ${
                          accentuate.launch_details_fr &&
                          window.location.pathname.includes('/fr')
                            ? accentuate.launch_details_fr
                            : accentuate.launch_details
                        }
                        ${action.button ? action.button : ''}
                      </div>
                    </div>
                  </div>
                </modal-dialog>
                  `
                    : ''
                }
              </div>
            </div>
          </div>
        </div>
      </li>`;

    releaseItemsWrapper.innerHTML = list_item;

    if (!action.date_id) {
      const element = document.querySelector('[data-tbd-wrapper]');
      element.style.display = 'block';
      document
        .querySelector('[data-tbd-items]')
        .appendChild(releaseItemsWrapper.querySelector('li'));
    } else if (action.date_id && action.button) {
      const d = action.date_id.split('-');
      const date = `${d[1]}/${d[0]}/${d[2]}`;
      let release = new Date(date);

      if (!this.isValidDate(release)) {
        release = new Date(date.replace(/-/g, '/'));
      }

      release.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      if (release < today && !accentuate.this_product_is_getting_raffle) {
        const element = document.querySelector('[data-released-wrapper]');
        element.style.display = 'block';

        const li = releaseItemsWrapper.querySelector('li');
        li.dataset.sortOrder = sortOrder;
        const img = li.querySelector('img');
        if (img) {
          img.setAttribute(
            'sizes',
            '(max-width: 375px) 349px, (max-width: 425px) 399px, (max-width: 768px) 169px, (max-width: 1024px) 233px, (max-width: 1440px) 290px, 290px'
          );
        }
        this.releasedItems.push(li);
      } else if (release >= today) {
        const parent_list = this.manageArticleList(release, date);
        document.querySelector(parent_list)?.appendChild(releaseItemsWrapper.querySelector('li'));
      }
    }

    if (this.indexArticle === this.lastIndex - 1) {
      if (this.featuredItems.length) {
        const featuredList = document.querySelector("[data-featured-items]");

        this.featuredItems
          .sort((a, b) => {
            const OrderA = parseInt(a.dataset.featuredSortOrder, 10) || 100;
            const OrderB = parseInt(b.dataset.featuredSortOrder, 10) || 100;
            return OrderA - OrderB;
          })
          .forEach((item, index) => {
            item.setAttribute("data-slide-index", index);
            featuredList.appendChild(item);
          });

        this.applyPriorityImageAttrs(featuredList);
      }

      if (this.releasedItems.length) {
        const releasedList = document.querySelector("[data-released-items]");

        this.releasedItems
          .sort((a, b) => {
            const OrderA = parseInt(a.dataset.sortOrder, 10) || 100;
            const OrderB = parseInt(b.dataset.sortOrder, 10) || 100;
            return OrderA - OrderB;
          })
          .forEach((item, index) => {
            item.setAttribute("data-slide-index", index);
            releasedList.appendChild(item);
          });
      }

      const hasMultipleFeaturedItems =
        document.querySelectorAll('[data-featured-items] > *').length > 1;
      const hasMultipleReleasedItems =
        document.querySelectorAll('[data-released-items] > *').length > 1;

      if (hasMultipleFeaturedItems) {
        this.initiateReleasedSlider('[data-featured-items]');
      }

      if (hasMultipleReleasedItems) {
        this.initiateReleasedSlider('[data-released-items]');
      }
    }

    this.indexArticle++;
  }

  initiateReleasedSlider(selector) {
    const slider = document.querySelector(selector);
    const sliderComponent = slider.closest('slider-component');
    sliderComponent.init();
  }

  isValidDate(d) {
    return d instanceof Date && !isNaN(d);
  }

  manageArticleList(date, data_id) {
    const days = window.theme.locale === 'fr_CA'
      ? window.releaseCalendarStrings.daysOfWeekFr
      : [
          'Sunday',
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
        ];
    const date_id = `<span>${days[date.getDay()]} </span> ${
      date.getMonth() + 1
    }/${date.getDate()}`;

    const current_lists = document.querySelectorAll('[data-dated-wrapper]');

    const newListWrapper = document.createElement('div');
    const new_list = `
      <div class="release__list" data-dated-wrapper=${data_id}>
        <h2 class="release__item-heading">${date_id}</h2>
        <ul data-dated-items=${data_id} class="grid grid--1-col"></ul>
      </div>`;
    newListWrapper.innerHTML = new_list;

    if (current_lists.length < 1) {
      document.querySelector('[data-featured-items-wrapper]').after(newListWrapper);
      return `[data-dated-items="${data_id}"]`;
    } else if (current_lists.length >= 1) {
      const dates = [];

      current_lists.forEach(function (index) {
        dates.push(index.dataset.datedWrapper);
      });

      if (dates.includes(data_id)) {
        return `[data-dated-items="${data_id}"]`;
      } else {
        dates.push(data_id);
        const sorted_dates = dates.sort(function (a, b) {
          return new Date(a) - new Date(b);
        });

        const current_index = sorted_dates.indexOf(data_id);
        const target_element =
          current_index - 1 != -1
            ? `[data-dated-wrapper="${sorted_dates[current_index - 1]}"]`
            : '[data-featured-items-wrapper]';

        document.querySelector(target_element).after(newListWrapper);
        return `[data-dated-items="${data_id}"]`;
      }
    }
  }
  getResolvedSortOrder({
    explicitOrder,
    releaseDay,
    createdAt,
    base = 1000000000000 
  }) {
    // 1. Explicit manual order (highest priority)
    const parsedOrder = parseInt(explicitOrder, 10);
    if (!isNaN(parsedOrder)) {
      return parsedOrder;
    }

    // 2. Release day fallback (Priority 2)
    if (releaseDay) {
      const d = releaseDay.split("-");
      const releaseDate = new Date(`${d[1]}/${d[0]}/${d[2]}`);
      if (this.isValidDate(releaseDate)) {
        return releaseDate.getTime();
      }
    }

    // 3. Article created date fallback (Priority 3)
    if (createdAt) {
      const createdDate = new Date(createdAt);
      if (this.isValidDate(createdDate)) {
        return createdDate.getTime();
      }
    }

    // 4. Priority 4 (very last)
    return base;
  }

  getActionType(accentuate, product, today) {
    const action = {};
    let release_time = false;

    if (
      accentuate.this_product_is_released_first == 'true' &&
      accentuate.release_time_ &&
      accentuate.release_day
    ) {
      release_time = this.getFullTime(
        accentuate.release_time_,
        accentuate.release_day
      );
    } else if (accentuate.this_product_is_getting_raffle == 'true') {
      if (accentuate.raffle_close_day && accentuate.raffle_close_time) {
        release_time = this.getFullTime(
          accentuate.raffle_close_time,
          accentuate.raffle_close_day
        );
      }
    }

    if (accentuate.this_product_is_released_first) {
      if (accentuate.release_day && release_time < Date.now()) {
        if (accentuate.collection_handle) {
          const collectionText = window.theme.locale === 'fr_CA' ? window.releaseCalendarStrings.shopCollectionFr : window.releaseCalendarStrings.shopCollection;
          action.button = `<a href="${this.localeUrl}/collections/${accentuate.collection_handle}" class="button button--primary" aria-label="${collectionText}">${collectionText}</a>`;
        } else if (product && product.handle) {
          if (product.available) {
            const shopNowText = window.theme.locale === 'fr_CA' ? window.releaseCalendarStrings.shopNowFr : window.releaseCalendarStrings.shopNow;
            action.button = `<a href="${product.url}" class="button button--primary" aria-label="${shopNowText}">${shopNowText}</a>`;
          } else {
            action.button = false;
          }
        } else {
          action.button = false;
        }
      } else {
        const notifyMeText = window.theme.locale === 'fr_CA' ? window.releaseCalendarStrings.notifyMeFr : window.releaseCalendarStrings.notifyMe;
        action.button = `<a href="${this.localeUrl}/pages/notification" class="button button--secondary">${notifyMeText}</a>`;
      }

      if (accentuate.release_day) {
        action.date_id = accentuate.release_day;
      }
    } else if (accentuate.this_product_is_getting_raffle) {
      if (
        accentuate.raffle_close_day &&
        accentuate.raffle_open_day &&
        accentuate.raffle_close_time &&
        accentuate.raffle_open_time
      ) {
        let raffleUrl = accentuate.raffle_page_url;
        if (Theme.settings.locale === 'fr') {
          raffleUrl = accentuate.raffle_page_url_french;
        }
        let open_time = this.getFullTime(
          accentuate.raffle_open_time,
          accentuate.raffle_open_day
        );
        if (open_time <= today && release_time >= today) {
          action.button = `<a href="${raffleUrl}" target="_blank" class="btn">Enter Raffle</a>`;
        } else if (open_time <= today && release_time <= today) {
          action.button = `<a class="btn btn--disabled">Raffle Closed</a>`;
          action.closed_raffle = 'release__raffle-closed';
        } else {
            action.button = `<a href="${this.localeUrl}/pages/notification" class="button button--secondary" aria-label="Notify Me">Notify Me</a>`;
        }
      } else {
        action.button = `<a href="${this.localeUrl}/pages/notification" class="button button--secondary" aria-label="Notify Me">Notify Me</a>`;
      }

      if (accentuate.raffle_release_day) {
        action.date_id = accentuate.raffle_release_day;
      }
    }

    return action;
  }

  getProductStock(handle) {
    return graphQL(`{
       productByHandle(handle: "${handle}") {
           title
           totalInventory
           availableForSale
           onlineStoreUrl
         }
       }`);
  }

  getFullTime(time_string, day_string) {
    const d = day_string.split('-');
    const date = `${d[1]}/${d[0]}/${d[2]}`;
    const time = time_string.replace('pm', ' PM').replace('am', ' AM');

    const newDate = new Date(date + ' ' + time + ' ' + this.timezone);

    return newDate;
  }
}
if(!customElements.get('release-calendar')){
  customElements.define('release-calendar', ReleaseCalendar);
}
